import URLParse from 'url-parse';
import { parseUrl } from '../utils/common';
import { assetUrl } from '../utils/assets';
import { pickCover } from '../utils/artwork';
import { withSdkParam } from '../utils/sdkParam';
import { Reaction } from '../constants/Reaction';

class GameScreen {
  defaults = {
    // The container where the game screen will be rendered
    container: 'game-screen',

    // you need to provide either gameId or gameUrl
    // if no `gameData` is used, the game won't be able to fetch from the server, so you don't need to provide gameId, you can provide gameUrl directly
    // gameId: "c5e52bb3cb734eee99def06c754dcf4f",
    // gameUrl: "https://revision.game8.io/5fd7a367a09548798fe9153017f55a62/",

    // gameHost: 'https://revision.game8.io',
    // imgHost: 'https://img.game8.io',

    // can be 'portrait' or 'landscape' or a function that returns either 'portrait' or 'landscape'
    orientation: (gameData) => {
      return gameData.orientation;
    },
    gameControls: {
      // can be a string that represents the game title or a function that returns the game title
      gameTitle: (gameData) => {
        return gameData.title;
      },
      // can be a string that represents the game thumb or a function that returns the game thumb
      gameThumb: (gameData) => {
        const uri = pickCover(gameData.covers);
        return uri ? parseUrl(this._options.imgHost, uri) : assetUrl('logo.png');
      },
      // You can provide a custom full screen button icon or hide the full screen button by setting fullScreenButton=false
      fullScreenButton: {
        icon: assetUrl('full-screen.svg'),
      },
      // Opt-in "theater mode" button. The host page owns the expanded state and
      // the layout change; the plugin only renders the button and reports clicks
      // through the onExpandToggle option. Off by default so embedders that have
      // no wider layout to expand into are unaffected.
      expandButton: false,
      reactionButton: true,
      // Determines the visibility of the game controls.
      // You can set the visibility for mobile, mobile_fullscreen, pc, pc_fullscreen or visibility=false to hide the game controls
      visibility: {
        mobile: true,
        mobile_fullscreen: false,
        pc: true,
        pc_fullscreen: true,
      },
    },
    // Button will float on mobile devices on fullscreen mode and will be hidden on PC
    floatingButton: {
      // Determines the visibility of the floating button.
      // You can set the visibility for mobile, mobile_fullscreen, pc, pc_fullscreen or visibility=false to hide the floating button
      visibility: {
        mobile: false,
        mobile_fullscreen: true,
        pc: false,
        pc_fullscreen: false,
      },
      // Default position of the floating button, default is bottom right corner
      defaultPosition: {
        right: '0px',
        bottom: '0px',
      },
    },
    onFullscreenExit: function () {},
  };

  constructor(options, _gameData, screenIndex = 1) {
    this._screenIndex = screenIndex;
    this._gameData = _gameData;
    this._options = this._deepExtend(this.defaults, options);
    this._expanded = !!this._options.expanded;
    this._wasFullScreen = false;
    if (!this._options.gameId && !this._options.gameUrl)
      throw new Error(
        '[g8plugin] You need to provide either gameId or gameUrl to create a GameScreen',
      );
    if (this._options.gameUrl)
      console.log('[g8plugin] Use preset gameUrl: ', this._options.gameUrl);
  }

  _deepExtend(target, ...sources) {
    for (const source of sources) {
      for (const key in source) {
        if (source.hasOwnProperty(key)) {
          if (typeof source[key] === 'object' && source[key] !== null) {
            target[key] = this._deepExtend(target[key] || {}, source[key]);
          } else {
            target[key] = source[key];
          }
        }
      }
    }
    return target;
  }

  _getGameUrl = function () {
    return withSdkParam(this._resolveGameUrl(), this._options.sdkJsFile);
  };

  _resolveGameUrl = function () {
    if (this._options.gameUrl) return this._options.gameUrl;
    if (!this._options.gameId)
      throw new Error('[g8plugin] gameId is required when gameUrl is not provided');

    let parser = new URLParse(location.href, true);
    parser.query = parser.query || {};
    let localUrl = parser.query['localUrl'];
    var gameUrl;
    if (localUrl) return localUrl;
    else {
      var platform = window.g8plugin.environment.platform;
      if (typeof this._gameData.gameUrl === 'string') gameUrl = this._gameData.gameUrl;
      else gameUrl = this._gameData.gameUrl[platform] || this._gameData.gameUrl['ALL'];
    }

    gameUrl = parseUrl(this._options.gameHost, gameUrl);
    return gameUrl;
  };

  async _getGameTitle() {
    if (this._options.gameControls && this._options.gameControls.gameTitle) {
      if (typeof this._options.gameControls.gameTitle === 'function') {
        return this._options.gameControls.gameTitle(this._gameData);
      }
    }
    return this._options.gameControls.gameTitle;
  }

  async _getGameThumb() {
    if (this._options.gameControls && this._options.gameControls.gameThumb) {
      if (typeof this._options.gameControls.gameThumb === 'function') {
        return this._options.gameControls.gameThumb(this._gameData);
      }
    }
    return this._options.gameControls.gameThumb;
  }

  async _getOrientation() {
    if (this._options.orientation) {
      if (typeof this._options.orientation === 'function') {
        return this._options.orientation(this._gameData);
      }
    }
    return this._options.orientation;
  }

  async init() {
    this._userContainer = document.getElementById(this._options.container);

    const container = document.createElement('div');
    container.id = 'game-screen-' + this._screenIndex;
    container.classList.add('ga-game-screen');
    this._container = container;
    this._userContainer.innerHTML = '';
    this._userContainer.appendChild(this._container);

    this._gameContainer = await this._createGameContainer();
    this._splashContainer = await this._createSplash();
    this._gameContainer.appendChild(this._splashContainer);
    this._container.appendChild(this._gameContainer);

    this._floatingButton = this._createFloatingButton();
    if (this._floatingButton) {
      this._gameContainer.appendChild(this._floatingButton);
      if (
        !this._checkVisibility(
          this._options.floatingButton && this._options.floatingButton.visibility,
        )
      )
        this._hideFloatingButton();
    }

    this._gameControls = await this._createGameControls();
    if (this._gameControls) {
      this._container.appendChild(this._gameControls);
      if (
        !this._checkVisibility(this._options.gameControls && this._options.gameControls.visibility)
      )
        this._hideGameControls();
    }

    if (this._isMobile()) this._registerFloatingButtonTouch();
    document.addEventListener(
      'webkitfullscreenchange',
      this._fullScreenChangeHandler.bind(this),
      false,
    );
    document.addEventListener(
      'mozfullscreenchange',
      this._fullScreenChangeHandler.bind(this),
      false,
    );
    document.addEventListener('fullscreenchange', this._fullScreenChangeHandler.bind(this), false);
    document.addEventListener(
      'MSFullscreenChange',
      this._fullScreenChangeHandler.bind(this),
      false,
    );
    window.addEventListener('resize', this._handleOrientationChange.bind(this));
    window.addEventListener('orientationchange', this._handleOrientationChange.bind(this));

    // Listen for fullscreen request from the game
    window.addEventListener('message', (event) => {
      var type = event.data.type;
      var method = event.data.method;
      if (type === 'game8') {
        switch (method) {
          case 'requestFullScreen':
            this._requestFullScreen();
            break;
          case 'exitFullScreen':
            this._exitFullScreen();
            break;
        }
      }
    });
  }

  async destroy() {
    document.removeEventListener(
      'webkitfullscreenchange',
      this._fullScreenChangeHandler.bind(this),
      false,
    );
    document.removeEventListener(
      'mozfullscreenchange',
      this._fullScreenChangeHandler.bind(this),
      false,
    );
    document.removeEventListener(
      'fullscreenchange',
      this._fullScreenChangeHandler.bind(this),
      false,
    );
    document.removeEventListener(
      'MSFullscreenChange',
      this._fullScreenChangeHandler.bind(this),
      false,
    );
    window.removeEventListener('resize', this._handleOrientationChange.bind(this));
    window.removeEventListener('orientationchange', this._handleOrientationChange.bind(this));
    this._container.innerHTML = '';
  }

  _isMobile() {
    var agent = navigator.userAgent || navigator.vendor || window.opera;
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone|IEMobile|Opera Mini/i.test(agent))
      return true;
    return false;
  }

  _checkVisibility(visibility) {
    if (!visibility) return false;
    const isMobile = this._isMobile();
    const isPc = !isMobile;
    const isFullScreen = this._isFullScreen();
    if (isMobile && isFullScreen) return visibility['mobile_fullscreen'];
    if (isMobile) return visibility.mobile;
    if (isPc && isFullScreen) return visibility['pc_fullscreen'];
    if (isPc) return visibility.pc;
    return false;
  }

  _nativeFullScreenElement() {
    return (
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement ||
      null
    );
  }

  _isFullScreen() {
    return !!this._nativeFullScreenElement() || document.body.classList.contains('isfullscreen');
  }

  _requestFullScreen() {
    // Fullscreen the whole document, not this._container. The AFG ad overlay is
    // appended to document.body, outside the container, and a browser paints only
    // the fullscreen element's subtree — so container fullscreen made ads render
    // invisibly, leaving the player nothing to dismiss and adBreakDone never firing.
    var element = document.documentElement;
    var request =
      element.requestFullscreen ||
      element.mozRequestFullScreen ||
      element.webkitRequestFullscreen ||
      element.msRequestFullscreen;

    if (!request) {
      // No Fullscreen API (iOS Safari): body.isfullscreen alone drives the layout.
      document.body.classList.add('isfullscreen');
      this._syncFullScreenUI();
      return;
    }

    // The class is applied from fullscreenchange instead of here, so a request the
    // browser refuses doesn't leave the page in a fake fullscreen state.
    try {
      var result = request.call(element);
      if (result && typeof result.catch === 'function') result.catch(function () {});
    } catch (error) {
      console.warn('g8plugin: fullscreen request refused', error);
    }
  }

  _exitFullScreen() {
    var exit =
      document.exitFullscreen ||
      document.mozCancelFullScreen ||
      document.webkitExitFullscreen ||
      document.msExitFullscreen;

    if (!exit || !this._nativeFullScreenElement()) {
      document.body.classList.remove('isfullscreen');
      this._syncFullScreenUI();
      return;
    }

    try {
      var result = exit.call(document);
      if (result && typeof result.catch === 'function') result.catch(function () {});
    } catch (error) {
      console.warn('g8plugin: exit fullscreen failed', error);
    }
  }

  _toggleFullScreen() {
    if (this._isFullScreen()) this._exitFullScreen();
    else this._requestFullScreen();
  }

  _fullScreenChangeHandler() {
    // Esc and the browser's own exit button never go through _exitFullScreen, so the
    // class the layout CSS keys on is mirrored from the native state here.
    if (this._nativeFullScreenElement()) document.body.classList.add('isfullscreen');
    else document.body.classList.remove('isfullscreen');
    this._syncFullScreenUI();
  }

  // Runs after the fullscreen state has settled, from both the native
  // fullscreenchange event and the iOS class-only fallback, so the exit
  // detection below sees every way out of fullscreen.
  _syncFullScreenUI() {
    const isFullScreen = this._isFullScreen();
    const exitedFullScreen = this._wasFullScreen && !isFullScreen;
    this._wasFullScreen = isFullScreen;

    if (this._checkVisibility(this._options.gameControls && this._options.gameControls.visibility))
      this._showGameControls();
    else this._hideGameControls();
    if (
      this._checkVisibility(this._options.floatingButton && this._options.floatingButton.visibility)
    )
      this._showFloatingButton();
    else this._hideFloatingButton();
    this._handleOrientationChange();
    if (this._gameFrame) this._gameFrame.focus();
    if (exitedFullScreen && this._isMobile()) this._options.onFullscreenExit();
  }

  _hideRotationMessage() {
    const rotationMessageContainer = document.querySelector('.rotation-message-container');
    if (rotationMessageContainer) rotationMessageContainer.style.display = 'none';
  }

  _showRotationMessage() {
    let rotationMessageContainer = document.querySelector('.rotation-message-container');
    if (rotationMessageContainer) {
      rotationMessageContainer.style.display = 'flex';
      return;
    }
    rotationMessageContainer = document.createElement('div');
    rotationMessageContainer.classList.add('rotation-message-container');

    const rotationTop = document.createElement('div');
    rotationTop.classList.add('rotation-top');
    rotationMessageContainer.appendChild(rotationTop);

    const rotationIcon = document.createElement('img');
    rotationIcon.src = assetUrl('rotate-phone.svg');
    rotationIcon.classList.add('rotation-icon');
    rotationTop.appendChild(rotationIcon);

    const rotationMessage = document.createElement('strong');
    rotationMessage.classList.add('rotation-message');
    rotationMessage.textContent = 'Rotate your device to play like a pro';
    rotationTop.appendChild(rotationMessage);

    const rotationClose = document.createElement('span');
    rotationClose.classList.add('rotation-close');
    rotationClose.textContent = 'Got it';
    rotationClose.onclick = () => this._hideRotationMessage();
    rotationMessageContainer.appendChild(rotationClose);

    this._container.appendChild(rotationMessageContainer);
  }

  async _handleOrientationChange() {
    let deviceOrientation = window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
    deviceOrientation = deviceOrientation.toLowerCase();

    let gameOrientation = await this._getOrientation();
    let shouldShowRotateMessage = false;
    if (this._isFullScreen() && this._isMobile() && gameOrientation) {
      gameOrientation = gameOrientation.toLowerCase();
      const isLandscapeGame = gameOrientation === 'landscape';
      const isPortraitGame = gameOrientation === 'portrait';

      shouldShowRotateMessage =
        (!isLandscapeGame && deviceOrientation === 'landscape') ||
        (!isPortraitGame && deviceOrientation === 'portrait');
    }
    if (shouldShowRotateMessage) {
      // try to lock the screen orientation if the browser supports it
      if (window.screen && window.screen.orientation && window.screen.orientation.lock) {
        window.screen.orientation.lock(gameOrientation).catch((e) => {
          // fallback to show the message if the screen orientation lock fails
          this._showRotationMessage();
        });
      } else {
        // fallback to show the message if the browser does not support screen orientation lock
        this._showRotationMessage();
      }
    } else this._hideRotationMessage();
  }

  async _createGameContainer() {
    const gameFrame = document.createElement('div');
    gameFrame.id = 'game-container' + this._screenIndex;
    gameFrame.style.position = 'relative';
    gameFrame.style.flexGrow = '1';
    gameFrame.style.overflow = 'hidden';
    gameFrame.style.backgroundColor = '#dfdfdf';
    gameFrame.style.display = 'flex';
    gameFrame.style.justifyContent = 'center';
    gameFrame.style.alignItems = 'center';
    return gameFrame;
  }

  async _createSplash() {
    const splash = document.createElement('div');
    splash.id = 'splash-' + this._screenIndex;
    return splash;
  }

  async showGameFrame() {
    const iframe = document.createElement('iframe');

    // Game flash cũ hay có splash logo nhà phát triển gắn getURL(_blank) tới site
    // đã chết, user bấm nhầm là văng sang tab 404. Không bật allow-popups để chặn
    // tab mới; không bật allow-top-navigation để game không cướp được trang cha.
    // Giữ allow-same-origin vì game cần localStorage (save game) và postMessage.
    if (this._gameData.type === 'FLASH') {
      iframe.setAttribute(
        'sandbox',
        'allow-scripts allow-same-origin allow-forms allow-pointer-lock allow-orientation-lock allow-modals',
      );
    }

    iframe.title = await this._getGameTitle();
    iframe.src = this._getGameUrl();
    iframe.style.backgroundColor = '#dfdfdf';
    iframe.style.display = 'block';
    iframe.style.margin = '0 auto';
    iframe.width = '100%';
    iframe.height = '100%';
    iframe.style.border = 'none';

    const scaleFrame = () => {
      var frameWidth = iframe.parentElement.offsetWidth;
      var frameHeight = iframe.parentElement.offsetHeight;
      var gameWidth = this._gameData.width;
      var gameHeight = this._gameData.height;

      const scaleWidth = frameWidth / gameWidth;
      const scaleHeight = frameHeight / gameHeight;
      const scale = Math.min(scaleWidth, scaleHeight);

      iframe.width = this._gameData.width;
      iframe.height = this._gameData.height;

      iframe.style.zoom = scale;
    };

    if (!this._gameData.isResponsesive) {
      requestAnimationFrame(() => {
        scaleFrame();
        document.addEventListener('webkitfullscreenchange', scaleFrame.bind(this));
        document.addEventListener('mozfullscreenchange', scaleFrame.bind(this));
        document.addEventListener('fullscreenchange', scaleFrame.bind(this));
        document.addEventListener('MSFullscreenChange', scaleFrame.bind(this));
        window.addEventListener('orientationchange', scaleFrame.bind(this));
      });
    }
    this._gameFrame = iframe;
    this._gameContainer.appendChild(iframe);
  }

  _createFloatingButton() {
    var floatingButton = document.createElement('div');
    floatingButton.id = 'floating-button-' + this._screenIndex;
    floatingButton.classList.add('floating-button');
    floatingButton.onclick = () => this._toggleFullScreen();
    floatingButton.innerHTML = `<button type="button"><img src="${assetUrl('full-screen.svg')}" alt="Show full screen"/></button>`;

    if (this._options.floatingButton && this._options.floatingButton.defaultPosition) {
      const { left, right, top, bottom } = this._options.floatingButton.defaultPosition;
      // if (left) floatingButton.style.left = left;
      // if (right) floatingButton.style.right = right;
      floatingButton.style.right = '0px';
      if (top) floatingButton.style.top = top;
      if (bottom) floatingButton.style.bottom = bottom;
    }
    return floatingButton;
  }

  _showFloatingButton() {
    this._floatingButton.classList.remove('hidden');
  }

  _hideFloatingButton() {
    this._floatingButton.classList.add('hidden');
  }

  _registerFloatingButtonTouch() {
    requestAnimationFrame(() => {
      var button = this._floatingButton;
      button.addEventListener('touchmove', (e) => {
        e.preventDefault();
        var parent = button.parentElement;
        var touchLocation = e.targetTouches[0];
        var parentLocation = {
          x: parent.getBoundingClientRect().left,
          y: parent.getBoundingClientRect().top,
        };
        var parentRange = {
          x: {
            min: parentLocation.x,
            max: parentLocation.x + parent.clientWidth,
          },
          y: {
            min: parentLocation.y,
            max: parentLocation.y + parent.clientHeight,
          },
        };
        var posX, posY;
        // if (touchLocation.clientX < parentRange.x.min + button.clientWidth / 2)
        // 	posX = 0;
        // else if (touchLocation.clientX > parentRange.x.max - button.clientWidth / 2)
        // 	posX = parent.clientWidth - button.clientWidth;
        // else
        // 	posX = touchLocation.clientX - parentRange.x.min - button.clientWidth / 2;

        if (touchLocation.clientY < parentRange.y.min + button.clientHeight / 2) posY = 0;
        else if (touchLocation.clientY > parentRange.y.max - button.clientHeight / 2)
          posY = parent.clientHeight - button.clientHeight;
        else posY = touchLocation.clientY - parentRange.y.min - button.clientHeight / 2;
        // button.style.right = "unset";
        button.style.bottom = 'unset';
        // button.style.left = posX + 'px';
        button.style.top = posY + 'px';
      });
    });
  }

  async _createGameControls() {
    if (!this._options.gameControls) return;

    const gameTitle = await this._getGameTitle();
    const gameThumb = await this._getGameThumb();

    const gameControls = document.createElement('div');
    gameControls.id = 'game-controls-' + this._screenIndex;
    gameControls.style.zIndex = '0';
    gameControls.style.backgroundColor = 'rgba(32, 38, 56, 1)';
    gameControls.style.height = '53px';
    gameControls.classList.add('game-controls');

    // Left controls
    gameControls.innerHTML = `
			<div class="controls-left">
				<a href="javascript:void(0)" rel="nofollow">
					<img src="${gameThumb}?w=36&h=36&f=webp" loading="lazy" alt="${gameTitle}" />
					<span style="color: rgba(255, 255, 255, 1); font-size: 16px; font-weight: bold; margin-left: 8px">${gameTitle}</span>
				</a>
			</div>`;

    // Right controls
    const controlsRight = document.createElement('div');
    controlsRight.classList.add('controls-right');
    gameControls.appendChild(controlsRight);

    controlsRight.classList.add('controls-right');

    if (this._options.gameControls.reactionButton) {
      // Like and Dislike and Favorite buttons
      const likebtn = document.createElement('button');
      likebtn.classList.add('likebtn');
      likebtn.onclick = () => this.handleLikeBtnClicked();
      likebtn.innerHTML = `<svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
									<path d="M12 8H16.764C17.1049 8.00001 17.4401 8.08714 17.7378 8.25312C18.0355 8.4191 18.2859 8.65842 18.4651 8.94836C18.6444 9.2383 18.7465 9.56924 18.7619 9.90976C18.7773 10.2503 18.7054 10.5891 18.553 10.894L15.053 17.894C14.8869 18.2265 14.6314 18.5061 14.3152 18.7014C13.999 18.8968 13.6347 19.0002 13.263 19H9.246C9.083 19 8.92 18.98 8.761 18.94L5 18M12 8V3C12 2.46957 11.7893 1.96086 11.4142 1.58579C11.0391 1.21071 10.5304 1 10 1H9.905C9.405 1 9 1.405 9 1.905C9 2.619 8.789 3.317 8.392 3.911L5 9V18M12 8H10M5 18H3C2.46957 18 1.96086 17.7893 1.58579 17.4142C1.21071 17.0391 1 16.5304 1 16V10C1 9.46957 1.21071 8.96086 1.58579 8.58579C1.96086 8.21071 2.46957 8 3 8H5.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
								</svg>
								<span>Like Game</span>`;
      this._likebtn = likebtn;

      const dislikebtn = document.createElement('button');
      dislikebtn.classList.add('dislikebtn');
      dislikebtn.onclick = () => this.handleDislikeBtnClicked();
      dislikebtn.innerHTML = `<svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
										<path d="M14.9988 2L11.2388 1.06C11.0802 1.02025 10.9173 1.0001 10.7538 1H6.73577C6.36428 1.00001 6.00013 1.10349 5.68415 1.29884C5.36816 1.49419 5.11283 1.77369 4.94677 2.106L1.44677 9.106C1.2944 9.41092 1.2225 9.74972 1.23788 10.0902C1.25326 10.4308 1.35541 10.7617 1.53465 11.0516C1.71388 11.3416 1.96424 11.5809 2.26197 11.7469C2.5597 11.9129 2.89491 12 3.23577 12H7.99977H9.99977M14.9988 2L14.9998 11L11.6078 16.088C11.2108 16.683 10.9998 17.381 10.9998 18.096C10.9998 18.595 10.5948 19 10.0948 19H9.99877C9.46834 19 8.95963 18.7893 8.58456 18.4142C8.20949 18.0391 7.99877 17.5304 7.99877 17V12M14.9988 2H16.9998C17.5302 2 18.0389 2.21071 18.414 2.58579C18.7891 2.96086 18.9998 3.46957 18.9998 4V10C18.9998 10.5304 18.7891 11.0391 18.414 11.4142C18.0389 11.7893 17.5302 12 16.9998 12H14.4998" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
									</svg>
									<span>Dislike Game</span>`;
      this._dislikebtn = dislikebtn;

      const favoritebtn = document.createElement('button');
      favoritebtn.classList.add('favoritebtn');
      favoritebtn.onclick = () => this.handleFavoritBtnClicked();
      favoritebtn.innerHTML = `<svg width="22" height="18" viewBox="0 0 22 18" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path fill-rule="evenodd" clip-rule="evenodd" d="M2.12356 1.72373C3.09871 0.748879 4.42111 0.201239 5.79996 0.201239C7.17882 0.201239 8.50122 0.748879 9.47636 1.72373L11 3.24603L12.5236 1.72373C13.0032 1.22708 13.577 0.830928 14.2115 0.558401C14.8459 0.285875 15.5282 0.142427 16.2187 0.136427C16.9091 0.130427 17.5939 0.261996 18.2329 0.523457C18.872 0.784917 19.4526 1.17103 19.9408 1.65927C20.4291 2.14752 20.8152 2.72811 21.0766 3.36717C21.3381 4.00623 21.4697 4.69096 21.4637 5.38142C21.4577 6.07187 21.3142 6.75421 21.0417 7.38863C20.7692 8.02305 20.373 8.59685 19.8764 9.07653L11 17.9542L2.12356 9.07653C1.14871 8.10139 0.601074 6.77898 0.601074 5.40013C0.601074 4.02128 1.14871 2.69887 2.12356 1.72373Z" stroke="white"/>
									</svg>
									<span>Add to Favorite</span>`;
      this._favoritebtn = favoritebtn;

      const feedbackbtn = document.createElement('button');
      feedbackbtn.classList.add('feedbackbtn');
      feedbackbtn.onclick = () => this.handleFeedbackBtnClicked();
      feedbackbtn.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M11.4374 3H5.24997C4.00735 3 3 4.00734 3 5.24996V14.2502C3 15.4928 4.00736 16.5002 5.24999 16.5002L10.5565 16.5001L12.9376 21L14.8126 16.5002L18.7501 16.5001C19.9927 16.5001 21 15.4928 21 14.2502V12.0004M21 5.81274C21 7.36601 19.7408 8.62519 18.1875 8.62519C16.6343 8.62519 15.3751 7.36601 15.3751 5.81274C15.3751 4.25946 16.6343 3.00028 18.1875 3.00028C19.7408 3.00028 21 4.25946 21 5.81274Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
									</svg>
									<span>Feed back</span>`;
      this._feedbackbtn = feedbackbtn;

      controlsRight.appendChild(likebtn);
      controlsRight.appendChild(dislikebtn);
      controlsRight.appendChild(favoritebtn);
      controlsRight.appendChild(feedbackbtn);

      requestAnimationFrame(() => {
        this.fillUserReaction();
        this.fillUserFavorite();
      });
    }

    // Expand ("theater mode") button. Sits next to the full screen button but is
    // a purely presentational control: it never changes state on its own, it just
    // reports the click. The host page flips its own state and calls setExpanded()
    // back, so the icon can never drift out of sync with the real layout (the host
    // can also collapse via Esc, which the plugin never sees).
    if (this._options.gameControls.expandButton) {
      const expandButton = document.createElement('button');
      expandButton.type = 'button';
      expandButton.onclick = () => this._options.onExpandToggle?.();
      expandButton.classList.add('expand-btn');
      this._expandBtn = expandButton;
      this._renderExpandIcon();
      controlsRight.appendChild(expandButton);
    }

    // Full screen button
    if (this._options.gameControls.fullScreenButton) {
      const fullScreenButton = document.createElement('button');
      fullScreenButton.type = 'button';
      fullScreenButton.onclick = () => this._toggleFullScreen();
      fullScreenButton.classList.add('fullscreen-btn');

      // Use provided SVG
      fullScreenButton.innerHTML = `
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M14.0004 9.99958L21 3.00003M21 3.00003L15.8572 3M21 3.00003L20.9999 8.14263M10.0004 14L3.00044 21M3.00044 21L8.14326 21M3.00044 21L3.00051 15.8574M14.0004 14L21 20.9996M21 20.9996L21 15.8569M21 20.9996L15.8573 20.9995M10.0004 10.0004L3.00003 3.00003M3.00003 3.00003L3 8.14272M3.00003 3.00003L8.14275 3.0001" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
				</svg>
				<span>Fullscreen</span>
			`;
      controlsRight.appendChild(fullScreenButton);
    }
    return gameControls;
  }

  // Called by the host page whenever its expanded state changes, from either the
  // button or its own affordances (Esc, restoring a saved preference). Safe to
  // call before the controls exist: the button reads _expanded when it is built.
  setExpanded(expanded) {
    this._expanded = !!expanded;
    if (this._expandBtn) this._renderExpandIcon();
  }

  _renderExpandIcon() {
    const icon = this._expanded
      ? `<path d="M4 6L10 12L4 18M20 6L14 12L20 18M12 5V19" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`
      : `<path d="M10 6L4 12L10 18M14 6L20 12L14 18M12 5V19" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;
    this._expandBtn.innerHTML = `
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					${icon}
				</svg>
				<span>${this._expanded ? 'Collapse' : 'Expand'}</span>
			`;
  }

  _showGameControls() {
    this._gameControls.classList.remove('hidden');
  }

  _hideGameControls() {
    this._gameControls.classList.add('hidden');
  }

  _showLikedStatus() {
    this._likebtn.classList.add('active');
    this._dislikebtn.classList.remove('active');
  }

  _showDislikedStatus() {
    this._dislikebtn.classList.add('active');
    this._likebtn.classList.remove('active');
  }

  _hideLikedStatus() {
    this._likebtn.classList.remove('active');
    this._dislikebtn.classList.remove('active');
  }

  _hideDislikedStatus() {
    this._dislikebtn.classList.remove('active');
    this._likebtn.classList.remove('active');
  }

  async handleLikeBtnClicked() {
    if ((await this._options.getGameReaction()) !== Reaction.LIKE) {
      this._options.setGameReaction(Reaction.LIKE) && this._showLikedStatus();
    } else {
      this._options.setGameReaction(Reaction.UNLIKE) && this._hideLikedStatus();
    }
  }

  async handleDislikeBtnClicked() {
    if ((await this._options.getGameReaction()) !== Reaction.DISLIKE) {
      this._options.setGameReaction(Reaction.DISLIKE) && this._showDislikedStatus();
    } else {
      this._options.setGameReaction(Reaction.UNDISLIKE) && this._hideDislikedStatus();
    }
  }

  async fillUserReaction() {
    const dislikeBtn = document.querySelector('.dislikebtn');
    const likeBtn = document.querySelector('.likebtn');
    var userReaction = await this._options.getGameReaction();
    if (!userReaction) return;
    if (userReaction === Reaction.DISLIKE) this._showDislikedStatus();
    else if (userReaction === Reaction.LIKE) this._showLikedStatus();
    else if (userReaction === Reaction.UNDISLIKE) this._hideDislikedStatus();
    else if (userReaction === Reaction.UNLIKE) this._hideLikedStatus();
  }

  _showFavoriteStatus() {
    this._favoritebtn.classList.add('active');
  }

  _hideFavoriteStatus() {
    this._favoritebtn.classList.remove('active');
  }

  async handleFavoritBtnClicked() {
    if (await this._options.isFavorite()) {
      this._options.unfavoriteGame() && this._hideFavoriteStatus();
    } else {
      this._options.favoriteGame() && this._showFavoriteStatus();
    }
  }

  async fillUserFavorite() {
    var isFavorited = await this._options.isFavorite();
    if (isFavorited) {
      this._showFavoriteStatus();
    } else {
      this._hideFavoriteStatus();
    }
  }

  async handleFeedbackBtnClicked() {
    await this._options.toggleFeedback();
  }
}

export default GameScreen;
